import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '../useApiMutation';
import { apiDelete, apiPatch, apiPost, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';
import type { SchedulerCrew } from '@/types/scheduler';

export interface SaveCrewInput {
  name: string;
  timezone: string;
  technicianIds: string[];
  leadTechnicianId?: string;
}

/**
 * NOT-354: persist the current ad-hoc crew selection as a reusable Saved Crew.
 * Mirrors the crew-create route flow — create the crew, assign each technician,
 * then set the lead (the backend requires the lead to already be a member).
 */
export function useSaveCrew() {
  const queryClient = useQueryClient();
  return useApiMutation<SchedulerCrew, SaveCrewInput>({
    mutationFn: async ({ name, timezone, technicianIds, leadTechnicianId }) => {
      const crew = await apiPost<SchedulerCrew>(operationsClient, schedulerEndpoints.crews(), {
        name,
        timezone,
      });
      // Assign members + set the lead as follow-up requests. If any fails, roll back
      // the just-created crew so a partially-built (broken) saved crew can't be picked
      // later; then surface the original error.
      try {
        await Promise.all(
          technicianIds.map((technicianId) =>
            apiPost<void>(operationsClient, schedulerEndpoints.crewAssign(crew.id), {
              technicianId,
            }),
          ),
        );
        if (leadTechnicianId) {
          return await apiPatch<SchedulerCrew>(operationsClient, schedulerEndpoints.crew(crew.id), {
            leadTechnicianId,
          });
        }
        return crew;
      } catch (err) {
        try {
          await apiDelete(operationsClient, schedulerEndpoints.crew(crew.id));
        } catch {
          // best-effort rollback — surface the original failure regardless
        }
        throw err;
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: schedulerKeys.crews() });
    },
  });
}
